// Portal: Onboarding tracker. One card per site still working through
// the onboarding pipeline (onboarding_status != 'Done'), with a step
// progress bar. Answers "where are we at with the new store?" without
// an email to Dave.
//
// Stage names are the CUSTOMER-FRIENDLY versions of the internal 10
// stages -- same order, same data, no internal jargon (BT900 etc).
// Sites with NULL onboarding_status are legacy rows from before the
// tracker existed; they're treated as live and not shown.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// Internal stage value -> customer-facing label. ORDER MATTERS -- it
// mirrors ONBOARDING_STATUSES in the internal SiteForm.
const STAGES = [
  { key: 'Not started',                  label: 'Order received' },
  { key: 'Client added',                 label: 'Account created' },
  { key: 'Site configured',              label: 'Site configured' },
  { key: 'Bulloch config done & tested', label: 'POS connection configured & tested' },
  { key: 'BT900 Price Book imported',    label: 'Price book imported' },
  { key: 'Fuel Manager setup complete',  label: 'Fuel Manager set up' },
  { key: 'Site Ready email sent',        label: 'Site ready — confirmation sent' },
  { key: 'Training scheduled',           label: 'Training scheduled' },
  { key: 'Training complete',            label: 'Training complete' },
  { key: 'Done',                         label: 'Live' }
];

export default function PortalOnboarding() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/portal/sites')
      .then((res) => { if (!cancelled) setRows(res.data); })
      .catch(() => { if (!cancelled) setError('Failed to load onboarding status. Please try again.'); });
    return () => { cancelled = true; };
  }, []);

  const inProgress = useMemo(() => {
    if (!rows) return [];
    return rows.filter((s) =>
      s.onboarding_status && s.onboarding_status !== 'Done' && s.status === 'Active'
    );
  }, [rows]);

  return (
    <section className="page">
      <div className="page-header">
        <h2>New Site Onboarding</h2>
        {rows && (
          <span className="portal-count">
            {inProgress.length === 0
              ? 'All sites live'
              : `${inProgress.length} site${inProgress.length === 1 ? '' : 's'} in progress`}
          </span>
        )}
      </div>

      {error && <p className="portal-error">{error}</p>}
      {!rows && !error && <p className="placeholder-note">Loading…</p>}

      {rows && inProgress.length === 0 && (
        <p className="placeholder-note">
          No sites are currently being onboarded — your whole fleet is live. 🎉
          When a new site is in setup, its progress will appear here step by step.
        </p>
      )}

      {inProgress.map((s) => {
        const idx = Math.max(0, STAGES.findIndex((st) => st.key === s.onboarding_status));
        return (
          <div key={s.id} className="portal-onboard-card">
            <div className="portal-onboard-head">
              <div>
                <strong>{s.site_name}</strong>{' '}
                <span className="mono" style={{ color: 'var(--color-text-faint)' }}>{s.site_number}</span>
                <div style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
                  {[s.city, s.province_state].filter(Boolean).join(', ')}
                </div>
              </div>
              <div className="portal-onboard-current">
                Current step: <strong>{STAGES[idx].label}</strong>
                <span className="portal-onboard-frac mono"> {idx + 1} / {STAGES.length}</span>
              </div>
            </div>
            <ol className="portal-onboard-steps">
              {STAGES.map((st, i) => (
                <li
                  key={st.key}
                  className={
                    i < idx ? 'is-complete' : i === idx ? 'is-current' : ''
                  }
                >
                  <span className="portal-onboard-dot" aria-hidden="true">
                    {i < idx ? '✓' : ''}
                  </span>
                  <span className="portal-onboard-label">{st.label}</span>
                </li>
              ))}
            </ol>
          </div>
        );
      })}

      {rows && inProgress.length > 0 && (
        <p className="portal-footnote">
          Steps update as the XSite team completes each stage. Questions about
          a site in setup? Use the <a href="/portal/support">Support</a> tab.
        </p>
      )}
    </section>
  );
}
